import firebase from 'firebase/app'

import * as crud from './crud'
import { getUser, lockTransaction, unlockTransaction } from './users'
import getClient from './getClient'
import { assert } from '../helpers'


export const orderMapping = async (doc, data) => {
  const client = await getClient(data.client_id)

  return crud.getDefaultMapping(doc, {
    ...data,
    client,
  })
}


/**
 * Edit attribute of an order object
 * @param {String} orderId - The id of the order
 * @param {Values} values - List of editable fields
 * @returns {Promise}
 */
export const editOrder = async (orderId, values = {}, options) => {
  const db = firebase.firestore()

  return crud.edit(db.collection('orders').doc(orderId), values, options)
}


/**
 * Get an order by its id
 * @param {String} uid - The id of the order
 * @returns {Order} The order object
 */
export const getOrder = async (uid, options = {}) => {
  const db = firebase.firestore()

  return crud.get(db.collection('orders').doc(uid), {
    ...options,
    map: async (doc, data) => {
      data.lastTransaction = await crud.getAll(db.collection('transactions').where('order_id', '==', uid), {
        limit: 1,
        first: true,
      })

      return orderMapping(doc, data)
    },
  })
}


/**
 * Get users from an order
 * @param {*} uid - The id of the order
 * @param {*} options - The option
 * @returns {Array<User>} List of users
 */
export const getOrderUsers = async (uid, options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('orders').doc(uid).collection('users'), {
    ...options,
    map: async (doc) => {
      const user = await getUser(doc.id)

      return user
    },
  })
}


/**
 * Get a list of orders
 * @param {*} options - Options to apply
 * @returns {Array<Order>} List of orders
 */
export const getOrders = async (options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('orders'), {
    ...options,
    map: orderMapping,
  })
}


/**
 * Add a new Client
 * @param {ShopData} data - The data of  the shop
 * @returns {Shop} The shop object
 */
export const addOrder = async (data) => {
  const {
    users,
    client_id,
    users_count,
    ...orderData
  } = data
  const db = firebase.firestore()
  const batch = db.batch()
  const ref = db.collection('orders').doc()
  const client = await getClient(client_id)
  const expires_at = new Date()
  const usersToUnlockTransaction = []
  
  expires_at.setFullYear(expires_at.getFullYear() + 1)
  assert(users_count > 0 && users_count === users.length, 'Tous les utilisateurs ne sont pas rempli.')
  assert(!!client, 'Les informations du client sont introuvables.')

  batch.set(ref, {
    ...orderData,
    client_id,
    expires_at,
    users_count,
    created_at: new Date(),
    client_name: client.name,
  })
  
  for (let i = 0; i < users.length; i++) {
    let userId
    const userData = users[i]
    const user = await crud.get(db.collection('users').where('phone', '==', userData.phone))
    if (!user) {
      const userRef = db.collection('users').doc()
      batch.set(userRef, {
        client_id,
        order_id: ref.id,
        email: userData.email,
        phone: userData.phone,
        created_at: new Date(),
        client_name: client.name,
        balance: data.amount_per_user,
        transaction_available: true,
      })
      userId = userRef.id
    } else {
      userId = user.id
      usersToUnlockTransaction.push(userId)
      await lockTransaction(userId)

      batch.update(db.collection('users').doc(userId), {
        balance: (user.balance || 0) + data.amount_per_user,
      })
    }
    const balanceRef = db.collection('users').doc(userId).collection('wallets').doc()
    batch.set(balanceRef, {
      client_id,
      expires_at,
      expired: false,
      order_id: ref.id,
      created_at: new Date(),
      client_name: client.name,
      initial_amount: data.amount_per_user,
      available_amount: data.amount_per_user,
    })

    batch.set(db.collection('orders').doc(ref.id).collection('users').doc(userId), {
      client_id,
      user_id: userId,
      created_at: new Date(),
      client_name: client.name,
      balance_id: balanceRef.id,
      email: user ? user.email : userData.email,
      phone: user ? user.phone : userData.phone,
    })
  }

  await batch.commit()
  await Promise.all(usersToUnlockTransaction.map(userId => unlockTransaction(userId)))

  const snapshot = await db.collection('orders').doc(ref.id).get()
  const result = snapshot.data()

  return {
    ...result,
    id: snapshot.id,
    created_at: result.created_at.toDate(),
  }
}
